// src/features/client-portal/components/ClientDashboard/index.jsx
// ✅ تصميم جديد — بوابة الزبون

import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchClientBookings,
  fetchClientBookingDetail,
  clientChangePassword,
} from '../../../../api';
import { useAuth }          from '../../../auth/context/AuthContext';
import LoadingSpinner       from '../../../../shared/components/ui/LoadingSpinner';
import Modal, { ModalFooter } from '../../../../shared/components/ui/Modal';
import Button               from '../../../../shared/components/ui/Button';
import { formatDate }       from '../../../../shared/utils/formatters';
import styles               from './ClientDashboard.module.css';

// ── ثوابت ──────────────────────────────────────────────────
const BOOKING_TYPE_LABELS = {
  graduation:        'حفلات التخرج',
  wedding:           'الأعراس',
  kids:              'تصوير أطفال',
  event:             'مناسبات',
  special:           'طلب خاص',
  digital_marketing: 'تسويق إلكتروني',
};

const STATUS_META = {
  pending:     { label: 'قيد الانتظار', cls: styles.statusPending,    stripe: styles.stripePending    },
  confirmed:   { label: 'مؤكد',         cls: styles.statusConfirmed,  stripe: styles.stripeConfirmed  },
  in_progress: { label: 'قيد التنفيذ',  cls: styles.statusInProgress, stripe: styles.stripeInProgress },
  completed:   { label: 'مكتمل',        cls: styles.statusCompleted,  stripe: styles.stripeCompleted  },
  cancelled:   { label: 'ملغي',         cls: styles.statusCancelled,  stripe: styles.stripeCancelled  },
};

const TASK_META = {
  todo:        { label: 'مطلوبة',      dotCls: styles.dotTodo,       badgeCls: styles.badgeTodo       },
  in_progress: { label: 'قيد التنفيذ', dotCls: styles.dotInProgress, badgeCls: styles.badgeInProgress },
  done:        { label: 'مكتملة',      dotCls: styles.dotDone,       badgeCls: styles.badgeDone       },
  cancelled:   { label: 'ملغاة',       dotCls: styles.dotCancelled,  badgeCls: styles.badgeCancelled  },
};

// ── شارة الحالة ─────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] ?? STATUS_META.pending;
  return (
    <span className={`${styles.statusBadge} ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

// ── مكوّن المرحلة ────────────────────────────────────────────
const WorkflowStage = ({ stage }) => {
  const [open, setOpen] = useState(false);

  const total    = stage.tasks?.length ?? 0;
  const done     = stage.tasks?.filter(t => t.status === 'done').length ?? 0;
  const pct      = total ? Math.round((done / total) * 100) : 0;
  const isDone   = stage.is_shot && stage.is_edited && stage.is_posted;

  return (
    <div className={styles.stage}>
      <button
        type="button"
        className={`${styles.stageHeader} ${isDone ? styles.stageDone : ''}`}
        onClick={() => setOpen(o => !o)}
      >
        <span className={styles.stageNum}>{stage.order}</span>
        <span className={styles.stageName}>{stage.name}</span>

        {total > 0 && (
          <span className={styles.stageProgress}>
            <span className={styles.progressBar}>
              <span className={styles.progressFill} style={{ width: `${pct}%` }} />
            </span>
            <span className={styles.progressText}>{done}/{total}</span>
          </span>
        )}

        <span className={styles.stageChevron}>{open ? '▼' : '▶'}</span>
      </button>

      {open && (
        <div className={styles.taskList}>
          {total === 0 ? (
            <p className={styles.noTasks}>لا توجد مهام في هذه المرحلة.</p>
          ) : (
            stage.tasks.map(task => {
              const meta = TASK_META[task.status] ?? TASK_META.todo;
              const isDoneTask = task.status === 'done';
              return (
                <div key={task.id} className={styles.taskRow}>
                  <span className={`${styles.taskDot} ${meta.dotCls}`} />
                  <span className={`${styles.taskName} ${isDoneTask ? styles.taskNameDone : ''}`}>
                    {task.title}
                  </span>
                  <span className={`${styles.taskBadge} ${meta.badgeCls}`}>
                    {meta.label}
                  </span>
                  {task.due_date && (
                    <span className={styles.taskDue}>
                      {formatDate(task.due_date)}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ── بطاقة الحجز ─────────────────────────────────────────────
const BookingCard = ({ booking }) => {
  const [expanded, setExpanded] = useState(false);
  const [detail,   setDetail]   = useState(null);
  const [loading,  setLoading]  = useState(false);

  const handleToggle = useCallback(async () => {
    if (detail) { setExpanded(o => !o); return; }
    setLoading(true);
    try {
      const res = await fetchClientBookingDetail(booking.id);
      setDetail(res.data);
      setExpanded(true);
    } catch { /* تجاهل */ }
    finally { setLoading(false); }
  }, [booking.id, detail]);

  const statusMeta = STATUS_META[booking.status] ?? STATUS_META.pending;
  const typeLabel  = BOOKING_TYPE_LABELS[booking.booking_type] ?? booking.booking_type_display;

  return (
    <div className={styles.bookingCard}>

      {/* شريط اللون العلوي */}
      <div className={`${styles.cardStripe} ${statusMeta.stripe}`} />

      {/* رأس البطاقة */}
      <div className={styles.cardHead}>
        <div className={styles.cardMeta}>
          <span className={styles.cardType}>{typeLabel}</span>
          <div className={styles.cardInfo}>
            <span className={styles.cardInfoText}>{formatDate(booking.booking_date)}</span>
            {booking.shooting_location && (
              <>
                <span className={styles.cardInfoDot} />
                <span className={styles.cardInfoText}>{booking.shooting_location}</span>
              </>
            )}
          </div>
        </div>

        <div className={styles.cardRight}>
          <StatusBadge status={booking.status} />
          <button
            type="button"
            className={`${styles.btnToggle} ${expanded ? styles.btnToggleOpen : ''}`}
            onClick={handleToggle}
            disabled={loading}
          >
            {loading ? 'جاري التحميل...' : expanded ? 'إخفاء خطة العمل' : 'عرض خطة العمل'}
          </button>
        </div>
      </div>

      {/* ملاحظات */}
      {booking.notes && (
        <p className={styles.cardNotes}>{booking.notes}</p>
      )}

      {/* خطة العمل */}
      {expanded && detail && (
        <div className={styles.workflowSection}>
          <h4 className={styles.workflowTitle}>خطة العمل</h4>
          {!detail.deliverables?.length ? (
            <p className={styles.noTasks}>لا توجد مراحل لهذا الحجز.</p>
          ) : (
            [...detail.deliverables]
              .sort((a, b) => a.order - b.order)
              .map(stage => (
                <WorkflowStage key={stage.id} stage={stage} />
              ))
          )}
        </div>
      )}
    </div>
  );
};

// ── مودال تغيير كلمة المرور ─────────────────────────────────
const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [oldPass, setOldPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { logout } = useAuth();

  const reset = () => {
    setOldPass(''); setNewPass(''); setConfirm('');
    setError(''); setSuccess('');
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSave = async () => {
    setError(''); setSuccess('');
    if (!oldPass || !newPass || !confirm) {
      setError('يرجى تعبئة جميع الحقول.'); return;
    }
    if (newPass !== confirm) {
      setError('كلمة المرور الجديدة وتأكيدها غير متطابقتين.'); return;
    }
    if (newPass.length < 4) {
      setError('كلمة المرور يجب أن تكون 4 أحرف على الأقل.'); return;
    }
    setLoading(true);
    try {
      const res = await clientChangePassword({ old_password: oldPass, new_password: newPass });
      if (res.data?.token) {
        localStorage.setItem('access_token', res.data.token);
        localStorage.setItem('client_token', res.data.token);
      }
      setSuccess('تم تغيير كلمة المرور بنجاح! سيتم تسجيل الخروج...');
      setTimeout(() => logout(), 2000);
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'فشل في تغيير كلمة المرور.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="تغيير كلمة المرور"
      size="sm"
      footer={
        <ModalFooter
          onClose={handleClose}
          onSave={handleSave}
          saveLabel="حفظ"
          loading={loading}
        />
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <input
          type="password"
          placeholder="كلمة المرور الحالية"
          value={oldPass}
          onChange={e => setOldPass(e.target.value)}
          autoFocus
          autoComplete="current-password"
        />
        <input
          type="password"
          placeholder="كلمة المرور الجديدة"
          value={newPass}
          onChange={e => setNewPass(e.target.value)}
          autoComplete="new-password"
        />
        <input
          type="password"
          placeholder="تأكيد كلمة المرور الجديدة"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          autoComplete="new-password"
        />
        {newPass && confirm && (
          <small style={{
            fontSize:   'var(--font-size-xs)',
            fontWeight: 'var(--font-semibold)',
            color: newPass === confirm
              ? 'var(--color-success-text)'
              : 'var(--color-danger-text)',
          }}>
            {newPass === confirm ? '✓ كلمتا المرور متطابقتان' : '✗ كلمتا المرور غير متطابقتين'}
          </small>
        )}
        {error && (
          <p style={{
            margin: 0, padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-danger-subtle)',
            border: '0.5px solid var(--color-danger-muted)',
            borderRight: '3px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-danger-text)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-semibold)',
          }}>
            {error}
          </p>
        )}
        {success && (
          <p style={{
            margin: 0, padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-success-subtle)',
            border: '0.5px solid var(--color-success-muted)',
            borderRight: '3px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-success-text)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-semibold)',
          }}>
            {success}
          </p>
        )}
      </div>
    </Modal>
  );
};

// ══════════════════════════════════════════════════════════
// الصفحة الرئيسية
// ══════════════════════════════════════════════════════════
const ClientDashboard = () => {
  const { loggedInUser, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [pwModal,  setPwModal]  = useState(false);

  const clientName = loggedInUser?.client?.name
    || loggedInUser?.first_name
    || 'العميل';

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetchClientBookings();
        setBookings(res.data.bookings || []);
      } catch {
        setError('فشل في تحميل الحجوزات. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingSpinner message="جاري تحميل حجوزاتك..." />;

  const activeCount = bookings.filter(
    b => b.status === 'in_progress' || b.status === 'confirmed' || b.status === 'pending'
  ).length;

  return (
    <div className={styles.container}>

      {/* ── هيدر الترحيب ── */}
      <div className={styles.hero}>
        <div className={styles.heroText}>
          <h1 className={styles.greeting}>مرحباً، {clientName}</h1>
          <p className={styles.subtitle}>
            {bookings.length === 0
              ? 'لا توجد حجوزات حالياً'
              : activeCount > 0
                ? `لديك ${activeCount} حجز نشط من أصل ${bookings.length}`
                : `لديك ${bookings.length} حجز`}
          </p>
        </div>
        <div className={styles.heroActions}>
          <Button variant="secondary" size="sm" onClick={() => setPwModal(true)}>
            تغيير كلمة المرور
          </Button>
          <Button variant="danger" size="sm" onClick={logout}>
            تسجيل الخروج
          </Button>
        </div>
      </div>

      {/* ── رسالة خطأ ── */}
      {error && <div className={styles.errorMsg}>{error}</div>}

      {/* ── المحتوى ── */}
      {bookings.length === 0 && !error ? (
        <div className={styles.empty}>
          <h2 className={styles.emptyTitle}>لا توجد حجوزات</h2>
          <p className={styles.emptyText}>لم يتم تسجيل أي حجوزات لحسابك بعد.</p>
        </div>
      ) : (
        <div className={styles.bookingsList}>
          {bookings.map(booking => (
            <BookingCard key={booking.id} booking={booking} />
          ))}
        </div>
      )}

      {/* ── مودال كلمة المرور ── */}
      <ChangePasswordModal
        isOpen={pwModal}
        onClose={() => setPwModal(false)}
      />

    </div>
  );
};

export default ClientDashboard;